/* eslint-disable react/jsx-no-comment-textnodes */
import type { ReactNode, ReactElement } from 'react';

import { formatDistanceToNowStrict } from 'src/utils/date-fns';
// @mui
import {
  Avatar,
  AvatarGroup,
  Stack,
  Typography,
  ListItemText,
  ListItemButton,
  ListItemAvatar,
  Badge,
} from '@mui/material';
// @ts-ignore legacy boundary
import { NotificationsOffRounded } from '@mui/icons-material';
// components
import BadgeStatus from 'src/components/BadgeStatus';
import useChatClient from 'src/hooks/useChatClient';
import { format } from 'src/utils/date-fns';
import { useTranslation } from 'react-i18next';

export interface ChatNavItemProps {
  conversation?: Record<string, any>;
  [key: string]: any;
}
// ----------------------------------------------------------------------

export default function ChatNavItem({
  conversation,
  openNav,
  isSelected,
  onSelect,
}: ChatNavItemProps) {
  const { t } = useTranslation('translation');
  const { conversationParticipants } = useChatClient();

  const conversationId = conversation.id;
  const participants = conversationParticipants[conversationId];

  const lastActivity =
    conversation.lastMessage?.dateCreated ?? conversation.dateCreated;

  const isUnread =
    conversation.lastMessage &&
    (!conversation.lastReadTimestamp ||
      new Date(conversation.lastMessage.dateCreated) >
        new Date(conversation.lastReadTimestamp));

  if (!participants) {
    // Placeholder entry (no Firestore conversation yet)
    return (
      <ListItemButton
        disableGutters
        onClick={onSelect}
        sx={{
          py: 1.5,
          px: 2.5,
          ...(isSelected && {
            bgcolor: 'action.selected',
          }),
        }}
      >
        <ListItemAvatar
          sx={{
            width: 64,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Avatar sx={{ width: 48, height: 48 }} />
        </ListItemAvatar>

        {openNav && (
          <ListItemText
            primary={conversation.friendlyName
              ?.replace('Admin Chat:', `${t('chat.admin-chat')}:`)
              .replace('Team Chat:', `${t('chat.team-chat')}:`)}
            primaryTypographyProps={{
              noWrap: true,
              variant: 'subtitle2',
            }}
          />
        )}
      </ListItemButton>
    );
  }

  return (
    <ListItemButton
      disableGutters
      onClick={onSelect}
      sx={{
        py: 1.5,
        px: 2.5,
        ...(isSelected && {
          bgcolor: 'action.selected',
        }),
      }}
    >
      <ListItemAvatar
        sx={{
          width: 64,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {participants.length > 1 ? (
          <AvatarGroup max={2}>
            {participants.map((participant) => (
              <Avatar
                key={participant.identity}
                alt={participant.name}
                sx={{ width: 32, height: 32 }}
              />
            ))}
          </AvatarGroup>
        ) : (
          <Badge
            overlap="circular"
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            badgeContent={
              <BadgeStatus
                status={participants[0]?.user?.status || 'unknown'}
              />
            }
          >
            <Avatar
              key={participants[0]?.identity}
              alt={participants[0]?.name}
              sx={{ width: 48, height: 48 }}
            />
          </Badge>
        )}
      </ListItemAvatar>

      {openNav && (
        <>
          <ListItemText
            primary={conversation.friendlyName
              ?.replace('Admin Chat:', `${t('chat.admin-chat')}:`)
              .replace('Team Chat:', `${t('chat.team-chat')}:`)}
            primaryTypographyProps={{ noWrap: true, variant: 'subtitle2' }}
            secondary={format(
              new Date(conversation.dateCreated.toString()),
              'EEE MMM dd yyyy'
            )}
            secondaryTypographyProps={{
              noWrap: true,
              variant: isUnread ? 'subtitle2' : 'body2',
              color: isUnread ? 'text.primary' : 'text.secondary',
            }}
          />

          <Stack alignItems="flex-end" sx={{ ml: 2, height: 44 }}>
            <Stack
              direction="row"
              spacing={0.5}
              alignItems="center"
              sx={{ mb: 1.5 }}
            >
              {conversation.muted && (
                <NotificationsOffRounded
                  titleAccess={t('chat.muted-conversation', {
                    defaultValue: 'Notifications muted',
                  })}
                  sx={{ fontSize: 14, color: 'text.disabled' }}
                />
              )}
              <Typography
                noWrap
                variant="body2"
                component="span"
                sx={{
                  fontSize: 12,
                  color: 'text.disabled',
                }}
              >
                {formatDistanceToNowStrict(new Date(lastActivity), {
                  addSuffix: false,
                })}
              </Typography>
            </Stack>
            {isUnread && <BadgeStatus status="unread" size="small" />}
          </Stack>
        </>
      )}
    </ListItemButton>
  );
}
